import { useAuth } from "@/contexts/AuthContext";
import { useCallback, useEffect, useRef, useState } from "react";

interface Message {
  id: string;
  chat_id: string;
  sender_id: string;
  body: string;
  has_masked_link: boolean;
  attachments_json?: string;
  created_at: number;
}

interface UseChatPollingOptions {
  chatId: string;
  enabled?: boolean;
  initialInterval?: number;
  maxInterval?: number;
  onNewMessage?: (message: Message) => void;
}

const API_BASE = import.meta.env.VITE_API_BASE || "";

export function useChatPolling({
  chatId,
  enabled = true,
  initialInterval = 3000, // 3초
  maxInterval = 20000, // 최대 20초
  onNewMessage,
}: UseChatPollingOptions) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentIntervalRef = useRef(initialInterval);
  const isVisibleRef = useRef(true);
  const loadingRef = useRef(false); // 중복 요청 방지

  // Phase 10: Offline message queue
  const [pendingMessages, setPendingMessages] = useState<
    Array<{
      id: string;
      body: string;
      timestamp: number;
    }>
  >([]);

  // Phase 10: Load pending messages from localStorage on mount
  useEffect(() => {
    if (chatId) {
      const stored = localStorage.getItem(`chat_pending_${chatId}`);
      if (stored) {
        try {
          const pending = JSON.parse(stored);
          setPendingMessages(pending);
        } catch (e) {
          console.error("Failed to load pending messages:", e);
        }
      }
    }
  }, [chatId]);

  // Phase 10: Save pending messages to localStorage
  useEffect(() => {
    if (chatId && pendingMessages.length > 0) {
      localStorage.setItem(
        `chat_pending_${chatId}`,
        JSON.stringify(pendingMessages)
      );
    } else if (chatId && pendingMessages.length === 0) {
      localStorage.removeItem(`chat_pending_${chatId}`);
    }
  }, [chatId, pendingMessages]);

  // Phase 10: Retry pending messages when online (moved after sendMessage definition)

  const fetchMessages = useCallback(async () => {
    if (!enabled || !isVisibleRef.current || !chatId || loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const since =
        lastFetchTime > 0 ? lastFetchTime : Date.now() - 24 * 60 * 60 * 1000; // 24시간 전
      // Get current user ID from auth context or localStorage
      let userId = "current_user";
      try {
        const auth = (window as any).firebase?.auth?.();
        if (auth?.currentUser) {
          userId = auth.currentUser.uid;
        }
      } catch (e) {
        // Ignore auth errors
      }

      const url = `${API_BASE}/api/chat/fetch?chat_id=${chatId}&since=${since}`;

      // Get auth token if available (GET 요청이므로 Content-Type 제거)
      const headers: HeadersInit = {};

      // Try to get Firebase auth token (if user is logged in)
      if (user) {
        try {
          const token = await user.getIdToken();
          headers["Authorization"] = `Bearer ${token}`;
        } catch (e) {
          // Ignore auth errors - 읽기 전용으로 계속 진행
        }
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.statusText}`);
      }

      const data = await response.json();
      const newMessages: Message[] = data.messages || [];

      if (newMessages.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const uniqueNew = newMessages.filter((m) => !existingIds.has(m.id));

          if (uniqueNew.length > 0) {
            const sorted = [...prev, ...uniqueNew].sort(
              (a, b) => a.created_at - b.created_at
            );
            uniqueNew.forEach((msg) => onNewMessage?.(msg));
            return sorted;
          }
          return prev;
        });

        // 성공 시 간격 초기화
        currentIntervalRef.current = initialInterval;
      } else {
        // 새 메시지 없으면 백오프
        currentIntervalRef.current = Math.min(
          currentIntervalRef.current * 2,
          maxInterval
        );
      }

      setLastFetchTime(Date.now());
    } catch (err) {
      console.error("Chat polling error:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch messages");
      // 에러 시 백오프
      currentIntervalRef.current = Math.min(
        currentIntervalRef.current * 2,
        maxInterval
      );
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [
    chatId,
    enabled,
    lastFetchTime,
    initialInterval,
    maxInterval,
    onNewMessage,
  ]);

  // 포커스 감지 (fetchMessages 정의 후에 배치)
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      if (isVisibleRef.current && enabled) {
        // 다시 보일 때 즉시 폴링
        fetchMessages();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, fetchMessages]);

  // 폴링 시작/중지
  useEffect(() => {
    if (!enabled || !chatId || !isVisibleRef.current) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // 초기 로드
    fetchMessages();

    // 주기적 폴링 (최소 5초 보장)
    intervalRef.current = setInterval(() => {
      if (!loadingRef.current) {
        fetchMessages();
      }
    }, Math.max(currentIntervalRef.current, 5000));

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, chatId, fetchMessages]);

  // 간격 업데이트 (최소 5초 보장)
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (!loadingRef.current) {
          fetchMessages();
        }
      }, Math.max(currentIntervalRef.current, 5000));
    }
  }, [currentIntervalRef.current, fetchMessages]);

  const sendMessage = useCallback(
    async (body: string, turnstileToken?: string) => {
      if (!chatId || !body.trim()) return;

      // Phase 10: Optimistic update with temporary ID
      const tempId = `temp_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 9)}`;
      const optimisticMessage: Message = {
        id: tempId,
        chat_id: chatId,
        sender_id: "current_user", // Will be replaced by server response
        body: body.trim(),
        has_masked_link: false,
        created_at: Date.now(),
      };

      // Add optimistic message immediately
      setMessages((prev) => [...prev, optimisticMessage]);

      try {
        // Get auth token if available
        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };

        // Get Firebase auth token from AuthContext
        if (user) {
          try {
            const token = await user.getIdToken();
            headers["Authorization"] = `Bearer ${token}`;
          } catch (e) {
            console.warn("Failed to get auth token:", e);
          }
        }

        const response = await fetch(`${API_BASE}/api/chat/send`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            chat_id: chatId,
            sender_id: user?.uid || "current_user", // Will be replaced by server from auth token
            body: body.trim(),
            turnstile_token: turnstileToken,
          }),
        });

        if (!response.ok) {
          // Remove optimistic message on error
          setMessages((prev) => prev.filter((m) => m.id !== tempId));

          // Phase 10: Add to offline queue if network error
          if (!navigator.onLine || response.status >= 500) {
            setPendingMessages((prev) => [
              ...prev,
              {
                id: tempId,
                body: body.trim(),
                timestamp: Date.now(),
              },
            ]);
            throw new Error(
              "Network error. Message will be sent when connection is restored."
            );
          }

          throw new Error(`Failed to send message: ${response.statusText}`);
        }

        const data = await response.json();
        const newMessage: Message = data.message;

        // Replace optimistic message with real message
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? newMessage : m))
        );
        setLastFetchTime(Date.now());

        return newMessage;
      } catch (err) {
        // Remove optimistic message on error (if not already removed)
        setMessages((prev) => prev.filter((m) => m.id !== tempId));

        // Phase 10: Add to offline queue if network error
        if (
          err instanceof Error &&
          (err.message.includes("Network") || !navigator.onLine)
        ) {
          setPendingMessages((prev) => [
            ...prev,
            {
              id: tempId,
              body: body.trim(),
              timestamp: Date.now(),
            },
          ]);
        }

        console.error("Send message error:", err);
        throw err;
      }
    },
    [chatId, user]
  );

  return {
    messages,
    loading,
    error,
    sendMessage,
    refetch: fetchMessages,
    pendingCount: pendingMessages.length, // Phase 10: Expose pending message count
  };
}
